import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Switch } from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ViewAgenda as SlotIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as bannerService from '../services/banner.service';
import { toPersianDigits } from '../components/common/JalaliDatePicker';

import './EntityManagement.css';

interface BannerSlot {
  _id: string;
  name: string;
  code: string;
  description: string;
  page: string;
  position: string;
  platform: string;
  dimensions: {
    desktop: { width: number; height: number; aspectRatio: string };
    mobile: { width: number; height: number; aspectRatio: string };
    tablet?: { width: number; height: number; aspectRatio: string };
  };
  pricing: {
    basePrice: number;
    weekendMultiplier: number;
    peakHoursMultiplier: number;
    discounts: {
      days7: number;
      days14: number;
      days30: number;
    };
  };
  restrictions: {
    minDurationDays: number;
    maxDurationDays: number;
    requiresManualApproval: boolean;
  };
  isActive: boolean;
  isPremium: boolean;
  displayOrder: number;
  activeReservationsCount?: number;
}

// اسلات‌های ثابت و از پیش تعریف شده
const PREDEFINED_SLOTS = [
  { code: 'hero-main', name: 'بنر اصلی هیرو', section: 'hero' },
  { code: 'hero-small-1', name: 'بنر کوچک هیرو ۱', section: 'hero' },
  { code: 'hero-small-2', name: 'بنر کوچک هیرو ۲', section: 'hero' },
  { code: 'hero-bottom', name: 'بنر پایین هیرو چپ', section: 'hero' },
  { code: 'hero-bottom-2', name: 'بنر پایین هیرو راست', section: 'hero' },
  { code: 'promo-box-1', name: 'باکس تبلیغاتی ۱', section: 'middle' },
  { code: 'promo-box-2', name: 'باکس تبلیغاتی ۲', section: 'middle' },
  { code: 'promo-box-3', name: 'باکس تبلیغاتی ۳', section: 'middle' },
  { code: 'promo-box-7', name: 'باکس تبلیغاتی ۷', section: 'middle' },
  { code: 'promo-box-4', name: 'باکس تبلیغاتی ۴', section: 'bottom' },
  { code: 'promo-box-5', name: 'باکس تبلیغاتی ۵', section: 'bottom' },
  { code: 'promo-box-6', name: 'باکس تبلیغاتی ۶', section: 'bottom' },
  { code: 'promo-box-8', name: 'باکس تبلیغاتی ۸', section: 'bottom' },
];

const getSectionLabel = (section: string): string => {
  switch (section) {
    case 'hero':
      return 'Hero (بالای صفحه)';
    case 'middle':
      return 'تبلیغاتی میانی';
    case 'bottom':
      return 'تبلیغاتی پایین';
    default:
      return section;
  }
};

const getSectionForCode = (code: string): string => {
  const found = PREDEFINED_SLOTS.find((s) => s.code === code);
  return found?.section || 'other';
};

const formatPrice = (price: number): string => {
  return toPersianDigits((price || 0).toLocaleString('en-US'));
};

const BannerSlotManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [slots, setSlots] = useState<BannerSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, occupied: 0, available: 0 });

  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionFilter, setSectionFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Dialog state
  const [editorOpen, setEditorOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<BannerSlot | null>(null);
  const [detailsSlot, setDetailsSlot] = useState<BannerSlot | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [formData, setFormData] = useState({
    description: '',
    dimensions: {
      desktop: { width: 1200, height: 400, aspectRatio: '16:9' },
      mobile: { width: 375, height: 200, aspectRatio: '16:9' },
      tablet: { width: 768, height: 300, aspectRatio: '16:9' },
    },
    pricing: {
      basePrice: 100000,
      weekendMultiplier: 1.2,
      peakHoursMultiplier: 1.5,
      discounts: {
        days7: 10,
        days14: 15,
        days30: 25,
      },
    },
    restrictions: {
      minDurationDays: 1,
      maxDurationDays: 30,
      requiresManualApproval: false,
    },
    isActive: true,
    isPremium: false,
  });

  const fetchSlots = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bannerService.getAllBannerSlots();
      const slotsData: BannerSlot[] = response.docs || [];

      // Try to fetch active reservations count per slot (best-effort)
      let reservations: any[] = [];
      try {
        const resResp: any = await bannerService.getAllReservations({ status: 'active', limit: 1000 });
        reservations = resResp?.docs || resResp?.items || [];
      } catch (_) {
        reservations = [];
      }

      const countsBySlot: Record<string, number> = {};
      reservations.forEach((r: any) => {
        const slotId = typeof r.slotId === 'object' ? r.slotId?._id : r.slotId;
        if (slotId) countsBySlot[slotId] = (countsBySlot[slotId] || 0) + 1;
      });

      const decorated = slotsData.map((s) => ({
        ...s,
        activeReservationsCount: countsBySlot[s._id] || 0,
      }));

      setSlots(decorated);

      const activeCount = decorated.filter((s) => s.isActive).length;
      const occupiedCount = decorated.filter((s) => (s.activeReservationsCount || 0) > 0).length;
      setStats({
        total: decorated.length,
        active: activeCount,
        occupied: occupiedCount,
        available: activeCount - occupiedCount,
      });
    } catch (err: any) {
      enqueueSnackbar('خطا در بارگذاری جایگاه‌های بنر', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSectionFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const openEdit = (slot: BannerSlot) => {
    setEditingSlot(slot);
    setFormData({
      description: slot.description || '',
      dimensions: {
        ...slot.dimensions,
        tablet: slot.dimensions.tablet || { width: 768, height: 300, aspectRatio: '16:9' },
      },
      pricing: slot.pricing,
      restrictions: slot.restrictions,
      isActive: slot.isActive,
      isPremium: slot.isPremium,
    });
    setEditorOpen(true);
  };

  const openDetails = (slot: BannerSlot) => {
    setDetailsSlot(slot);
    setDetailsOpen(true);
  };

  const handleSave = async () => {
    try {
      if (!editingSlot) return;
      setActionLoading(true);

      const updateData = {
        description: formData.description,
        dimensions: formData.dimensions,
        pricing: formData.pricing,
        restrictions: formData.restrictions,
        isActive: formData.isActive,
        isPremium: formData.isPremium,
      };

      await bannerService.updateBannerSlot(editingSlot._id, updateData);
      enqueueSnackbar('تنظیمات با موفقیت بروزرسانی شد', { variant: 'success' });
      setEditorOpen(false);
      fetchSlots();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'خطا در ذخیره', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      await bannerService.toggleSlotActive(id, !isActive);
      enqueueSnackbar(`جایگاه ${!isActive ? 'فعال' : 'غیرفعال'} شد`, { variant: 'success' });
      fetchSlots();
    } catch (err: any) {
      enqueueSnackbar('خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  // Derive filtered
  const filtered = slots.filter((s) => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const hit = s.name?.toLowerCase().includes(q) || s.code?.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (sectionFilter) {
      const section = getSectionForCode(s.code);
      if (section !== sectionFilter) return false;
    }
    if (statusFilter === 'active' && !s.isActive) return false;
    if (statusFilter === 'inactive' && s.isActive) return false;
    if (statusFilter === 'occupied' && !(s.activeReservationsCount && s.activeReservationsCount > 0)) return false;
    if (statusFilter === 'available' && (!s.isActive || (s.activeReservationsCount || 0) > 0)) return false;
    return true;
  });

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const hasActiveFilters = !!searchTerm || !!sectionFilter || !!statusFilter;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت جایگاه‌های بنر</h1>
          <p className="entity-subtitle">پیکربندی جایگاه‌های تبلیغاتی سایت</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchSlots} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon purple"><SlotIcon /></div>
          <div>
            <p className="entity-stat-label">کل جایگاه‌ها</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.total))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.active))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><VisibilityIcon /></div>
          <div>
            <p className="entity-stat-label">اشغال شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.occupied))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><InfoIcon /></div>
          <div>
            <p className="entity-stat-label">در دسترس</p>
            <p className="entity-stat-value">{toPersianDigits(String(Math.max(0, stats.available)))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو بر اساس نام، کد یا توضیحات..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">بخش</label>
            <select
              className="filter-field"
              value={sectionFilter}
              onChange={(e) => { setSectionFilter(e.target.value); setPage(1); }}
            >
              <option value="">همه بخش‌ها</option>
              <option value="hero">Hero</option>
              <option value="middle">تبلیغاتی میانی</option>
              <option value="bottom">تبلیغاتی پایین</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="active">فعال</option>
              <option value="inactive">غیرفعال</option>
              <option value="occupied">اشغال شده</option>
              <option value="available">در دسترس</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {sectionFilter && (
              <span className="filter-tag">
                بخش: {getSectionLabel(sectionFilter)}
                <button onClick={() => setSectionFilter('')}>×</button>
              </span>
            )}
            {statusFilter && (
              <span className="filter-tag">
                وضعیت: {statusFilter === 'active' ? 'فعال' : statusFilter === 'inactive' ? 'غیرفعال' : statusFilter === 'occupied' ? 'اشغال شده' : 'در دسترس'}
                <button onClick={() => setStatusFilter('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : paginated.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><SlotIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">جایگاهی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>جایگاه</th>
                  <th>کد</th>
                  <th>موقعیت</th>
                  <th>قیمت روزانه</th>
                  <th>رزروهای فعال</th>
                  <th>وضعیت</th>
                  <th style={{ width: 140 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((slot) => {
                  const section = getSectionForCode(slot.code);
                  const occupied = (slot.activeReservationsCount || 0) > 0;
                  return (
                    <tr key={slot._id}>
                      <td>
                        <div className="entity-info-cell">
                          <div className="entity-image"><SlotIcon /></div>
                          <div className="entity-info">
                            <span className="entity-name">
                              {slot.name}
                              {slot.isPremium && <span style={{ marginRight: 6 }}>⭐</span>}
                            </span>
                            {slot.description && <span className="entity-desc">{slot.description}</span>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="cell-code">{slot.code}</span>
                      </td>
                      <td>
                        <span className={`tag tag-sm ${section === 'hero' ? 'tag-platform' : 'tag-active'}`}>
                          {getSectionLabel(section)}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {formatPrice(slot.pricing?.basePrice || 0)}
                          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginRight: 4 }}>تومان</span>
                        </div>
                      </td>
                      <td>
                        {occupied ? (
                          <span className="tag tag-sm tag-platform">
                            {toPersianDigits(String(slot.activeReservationsCount || 0))} رزرو
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>-</span>
                        )}
                      </td>
                      <td>
                        <span className={`tag tag-sm ${slot.isActive ? 'tag-active' : 'tag-inactive'}`}>
                          {slot.isActive ? 'فعال' : 'غیرفعال'}
                        </span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <Tooltip title="مشاهده جزئیات" arrow>
                            <button className="action-btn" onClick={() => openDetails(slot)}>
                              <VisibilityIcon />
                            </button>
                          </Tooltip>
                          <Tooltip title="ویرایش تنظیمات" arrow>
                            <button className="action-btn edit" onClick={() => openEdit(slot)}>
                              <EditIcon />
                            </button>
                          </Tooltip>
                          <Tooltip title={slot.isActive ? 'غیرفعال کردن' : 'فعال کردن'} arrow>
                            <button
                              className={`action-btn toggle ${slot.isActive ? 'active' : ''}`}
                              onClick={() => handleToggleActive(slot._id, slot.isActive)}
                            >
                              {slot.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {paginated.map((slot) => {
                const section = getSectionForCode(slot.code);
                const occupied = (slot.activeReservationsCount || 0) > 0;
                return (
                  <div key={slot._id} className={`entity-card ${!slot.isActive ? 'inactive' : ''}`}>
                    <div className="entity-card-header">
                      <div className="entity-card-image"><SlotIcon /></div>
                      <div className="entity-card-info">
                        <div className="entity-card-name">
                          {slot.name}
                          {slot.isPremium && <span style={{ marginRight: 6 }}>⭐</span>}
                        </div>
                        <div className="entity-card-desc">
                          <span className="cell-code">{slot.code}</span> • {getSectionLabel(section)}
                        </div>
                        <div className="entity-card-desc" style={{ marginTop: 4 }}>
                          {formatPrice(slot.pricing?.basePrice || 0)} تومان/روز
                          {occupied && <> • {toPersianDigits(String(slot.activeReservationsCount || 0))} رزرو فعال</>}
                        </div>
                      </div>
                      <div className="entity-card-status">
                        <span className={`tag tag-sm ${slot.isActive ? 'tag-active' : 'tag-inactive'}`}>
                          {slot.isActive ? 'فعال' : 'غیرفعال'}
                        </span>
                      </div>
                    </div>
                    <div className="entity-card-actions">
                      <button className="entity-card-btn" onClick={() => openDetails(slot)}>
                        <VisibilityIcon fontSize="small" /> جزئیات
                      </button>
                      <button className="entity-card-btn primary" onClick={() => openEdit(slot)}>
                        <EditIcon fontSize="small" /> ویرایش
                      </button>
                      <button className="entity-card-btn danger" onClick={() => handleToggleActive(slot._id, slot.isActive)}>
                        {slot.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        {slot.isActive ? 'غیرفعال' : 'فعال'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(totalCount))}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{toPersianDigits(String(page))} / {toPersianDigits(String(totalPages))}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={editorOpen}
        onClose={() => !actionLoading && setEditorOpen(false)}
        maxWidth="md"
        fullWidth
        className="bannerslot-edit-dialog"
      >
        <div className="edit-dialog-title">
          <span>
            ویرایش جایگاه: {editingSlot?.name}
            {editingSlot && (
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 400, marginRight: 8 }}>
                ({editingSlot.code})
              </span>
            )}
          </span>
          <button className="details-close" onClick={() => !actionLoading && setEditorOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {editingSlot && (
            <div className="edit-form">
              {/* General info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات عمومی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>کد</label>
                      <input type="text" value={editingSlot.code} disabled />
                    </div>
                    <div className="edit-field">
                      <label>صفحه</label>
                      <input type="text" value={editingSlot.page} disabled />
                    </div>
                    <div className="edit-field">
                      <label>موقعیت</label>
                      <input type="text" value={editingSlot.position} disabled />
                    </div>
                    <div className="edit-field">
                      <label>پلتفرم</label>
                      <input type="text" value={editingSlot.platform} disabled />
                    </div>
                    <div className="edit-field full">
                      <label>توضیحات</label>
                      <textarea
                        rows={2}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="توضیحات مختصر درباره این جایگاه..."
                        disabled={actionLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="edit-card">
                <div className="edit-card-header">قیمت‌گذاری</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>قیمت پایه روزانه (تومان)</label>
                      <input
                        type="number"
                        value={formData.pricing.basePrice}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: { ...formData.pricing, basePrice: parseInt(e.target.value) || 0 },
                          })
                        }
                        placeholder="100000"
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>ضریب آخر هفته</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.pricing.weekendMultiplier}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: { ...formData.pricing, weekendMultiplier: parseFloat(e.target.value) || 1 },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>ضریب ساعات شلوغی</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.pricing.peakHoursMultiplier}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: { ...formData.pricing, peakHoursMultiplier: parseFloat(e.target.value) || 1 },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>تخفیف ۷ روزه (٪)</label>
                      <input
                        type="number"
                        value={formData.pricing.discounts.days7}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              discounts: { ...formData.pricing.discounts, days7: parseInt(e.target.value) || 0 },
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>تخفیف ۱۴ روزه (٪)</label>
                      <input
                        type="number"
                        value={formData.pricing.discounts.days14}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              discounts: { ...formData.pricing.discounts, days14: parseInt(e.target.value) || 0 },
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>تخفیف ۳۰ روزه (٪)</label>
                      <input
                        type="number"
                        value={formData.pricing.discounts.days30}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              discounts: { ...formData.pricing.discounts, days30: parseInt(e.target.value) || 0 },
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dimensions */}
              <div className="edit-card">
                <div className="edit-card-header">ابعاد تصاویر</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label style={{ fontWeight: 600 }}>Desktop</label>
                    </div>
                    <div className="edit-field">
                      <label>عرض (px)</label>
                      <input
                        type="number"
                        value={formData.dimensions.desktop.width}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            dimensions: {
                              ...formData.dimensions,
                              desktop: { ...formData.dimensions.desktop, width: parseInt(e.target.value) || 0 },
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>ارتفاع (px)</label>
                      <input
                        type="number"
                        value={formData.dimensions.desktop.height}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            dimensions: {
                              ...formData.dimensions,
                              desktop: { ...formData.dimensions.desktop, height: parseInt(e.target.value) || 0 },
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>نسبت</label>
                      <input
                        type="text"
                        value={formData.dimensions.desktop.aspectRatio || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            dimensions: {
                              ...formData.dimensions,
                              desktop: { ...formData.dimensions.desktop, aspectRatio: e.target.value },
                            },
                          })
                        }
                        placeholder="16:9"
                        disabled={actionLoading}
                      />
                    </div>

                    <div className="edit-field full">
                      <label style={{ fontWeight: 600, marginTop: 8 }}>Mobile</label>
                    </div>
                    <div className="edit-field">
                      <label>عرض (px)</label>
                      <input
                        type="number"
                        value={formData.dimensions.mobile.width}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            dimensions: {
                              ...formData.dimensions,
                              mobile: { ...formData.dimensions.mobile, width: parseInt(e.target.value) || 0 },
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>ارتفاع (px)</label>
                      <input
                        type="number"
                        value={formData.dimensions.mobile.height}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            dimensions: {
                              ...formData.dimensions,
                              mobile: { ...formData.dimensions.mobile, height: parseInt(e.target.value) || 0 },
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>نسبت</label>
                      <input
                        type="text"
                        value={formData.dimensions.mobile.aspectRatio || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            dimensions: {
                              ...formData.dimensions,
                              mobile: { ...formData.dimensions.mobile, aspectRatio: e.target.value },
                            },
                          })
                        }
                        placeholder="16:9"
                        disabled={actionLoading}
                      />
                    </div>

                    <div className="edit-field">
                      <label>حداقل روز رزرو</label>
                      <input
                        type="number"
                        value={formData.restrictions.minDurationDays}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            restrictions: {
                              ...formData.restrictions,
                              minDurationDays: parseInt(e.target.value) || 1,
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                    <div className="edit-field">
                      <label>حداکثر روز رزرو</label>
                      <input
                        type="number"
                        value={formData.restrictions.maxDurationDays}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            restrictions: {
                              ...formData.restrictions,
                              maxDurationDays: parseInt(e.target.value) || 30,
                            },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Active state */}
              <div className="edit-card">
                <div className="edit-card-header">وضعیت فعال</div>
                <div className="edit-card-body">
                  <div className="edit-switches">
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">فعال</div>
                        <div className="edit-switch-desc">این جایگاه برای نمایش در سایت فعال باشد</div>
                      </div>
                      <Switch
                        size="small"
                        checked={formData.isActive}
                        onChange={(_, v) => setFormData({ ...formData, isActive: v })}
                        disabled={actionLoading}
                      />
                    </label>
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">ممتاز</div>
                        <div className="edit-switch-desc">جایگاه با اولویت و قیمت بالاتر</div>
                      </div>
                      <Switch
                        size="small"
                        checked={formData.isPremium}
                        onChange={(_, v) => setFormData({ ...formData, isPremium: v })}
                        disabled={actionLoading}
                      />
                    </label>
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">نیاز به تایید دستی</div>
                        <div className="edit-switch-desc">رزرو جدید باید توسط ادمین تایید شود</div>
                      </div>
                      <Switch
                        size="small"
                        checked={formData.restrictions.requiresManualApproval}
                        onChange={(_, v) =>
                          setFormData({
                            ...formData,
                            restrictions: { ...formData.restrictions, requiresManualApproval: v },
                          })
                        }
                        disabled={actionLoading}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setEditorOpen(false)} disabled={actionLoading}>
            انصراف
          </button>
          <button className="edit-btn submit" onClick={handleSave} disabled={actionLoading}>
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            ذخیره تغییرات
          </button>
        </div>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        className="bannerslot-details-dialog"
      >
        <div className="edit-dialog-title">
          <span>
            جزئیات جایگاه: {detailsSlot?.name}
            {detailsSlot && (
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 400, marginRight: 8 }}>
                ({detailsSlot.code})
              </span>
            )}
          </span>
          <button className="details-close" onClick={() => setDetailsOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {detailsSlot && (
            <div className="edit-form">
              {/* Quick overview */}
              <div className="edit-card">
                <div className="edit-card-header">نمای کلی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>وضعیت</label>
                      <div>
                        <span className={`tag tag-sm ${detailsSlot.isActive ? 'tag-active' : 'tag-inactive'}`}>
                          {detailsSlot.isActive ? 'فعال' : 'غیرفعال'}
                        </span>
                        {detailsSlot.isPremium && (
                          <span className="tag tag-sm tag-platform" style={{ marginRight: 6 }}>⭐ ممتاز</span>
                        )}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>بخش</label>
                      <div>
                        <span className="tag tag-sm tag-platform">
                          {getSectionLabel(getSectionForCode(detailsSlot.code))}
                        </span>
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>رزروهای فعال</label>
                      <div style={{ fontWeight: 600 }}>
                        {toPersianDigits(String(detailsSlot.activeReservationsCount || 0))}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>ترتیب نمایش</label>
                      <div>{toPersianDigits(String(detailsSlot.displayOrder ?? 0))}</div>
                    </div>
                    {detailsSlot.description && (
                      <div className="edit-field full">
                        <label>توضیحات</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>
                          {detailsSlot.description}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Pricing analytics */}
              <div className="edit-card">
                <div className="edit-card-header">قیمت‌گذاری</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>قیمت پایه روزانه</label>
                      <div style={{ fontWeight: 700, color: 'var(--color-primary, #B25373)' }}>
                        {formatPrice(detailsSlot.pricing?.basePrice || 0)} تومان
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>ضریب آخر هفته</label>
                      <div>×{toPersianDigits(String(detailsSlot.pricing?.weekendMultiplier ?? 1))}</div>
                    </div>
                    <div className="edit-field">
                      <label>ضریب ساعات شلوغی</label>
                      <div>×{toPersianDigits(String(detailsSlot.pricing?.peakHoursMultiplier ?? 1))}</div>
                    </div>
                    <div className="edit-field">
                      <label>تخفیف ۷ روزه</label>
                      <div>{toPersianDigits(String(detailsSlot.pricing?.discounts?.days7 ?? 0))}٪</div>
                    </div>
                    <div className="edit-field">
                      <label>تخفیف ۱۴ روزه</label>
                      <div>{toPersianDigits(String(detailsSlot.pricing?.discounts?.days14 ?? 0))}٪</div>
                    </div>
                    <div className="edit-field">
                      <label>تخفیف ۳۰ روزه</label>
                      <div>{toPersianDigits(String(detailsSlot.pricing?.discounts?.days30 ?? 0))}٪</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dimensions */}
              <div className="edit-card">
                <div className="edit-card-header">ابعاد</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>Desktop</label>
                      <div>
                        {toPersianDigits(String(detailsSlot.dimensions?.desktop?.width || 0))} × {toPersianDigits(String(detailsSlot.dimensions?.desktop?.height || 0))}
                        {detailsSlot.dimensions?.desktop?.aspectRatio && (
                          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginRight: 6 }}>
                            ({detailsSlot.dimensions.desktop.aspectRatio})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>Mobile</label>
                      <div>
                        {toPersianDigits(String(detailsSlot.dimensions?.mobile?.width || 0))} × {toPersianDigits(String(detailsSlot.dimensions?.mobile?.height || 0))}
                        {detailsSlot.dimensions?.mobile?.aspectRatio && (
                          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginRight: 6 }}>
                            ({detailsSlot.dimensions.mobile.aspectRatio})
                          </span>
                        )}
                      </div>
                    </div>
                    {detailsSlot.dimensions?.tablet && (
                      <div className="edit-field">
                        <label>Tablet</label>
                        <div>
                          {toPersianDigits(String(detailsSlot.dimensions.tablet.width || 0))} × {toPersianDigits(String(detailsSlot.dimensions.tablet.height || 0))}
                          {detailsSlot.dimensions.tablet.aspectRatio && (
                            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginRight: 6 }}>
                              ({detailsSlot.dimensions.tablet.aspectRatio})
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Restrictions */}
              <div className="edit-card">
                <div className="edit-card-header">محدودیت‌ها</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>حداقل روز رزرو</label>
                      <div>{toPersianDigits(String(detailsSlot.restrictions?.minDurationDays ?? 1))} روز</div>
                    </div>
                    <div className="edit-field">
                      <label>حداکثر روز رزرو</label>
                      <div>{toPersianDigits(String(detailsSlot.restrictions?.maxDurationDays ?? 30))} روز</div>
                    </div>
                    <div className="edit-field full">
                      <label>نیاز به تایید دستی</label>
                      <div>
                        <span className={`tag tag-sm ${detailsSlot.restrictions?.requiresManualApproval ? 'tag-platform' : 'tag-active'}`}>
                          {detailsSlot.restrictions?.requiresManualApproval ? 'بله' : 'خیر'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setDetailsOpen(false)}>بستن</button>
          {detailsSlot && (
            <button
              className="edit-btn submit"
              onClick={() => {
                setDetailsOpen(false);
                openEdit(detailsSlot);
              }}
            >
              <EditIcon fontSize="small" /> ویرایش
            </button>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default BannerSlotManagement;
